import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FilterMatchMode } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import SearchInputWithClear from 'ui/custom-controls/SearchInputWithClear';
import TablePageHeader from 'ui/shared/TablePageHeader';

import { RolesTemplate } from '../constants/RolesTemplate';
import { useDebounce } from '../hooks';
import useRoleUIsService from '../hooks/services/useRoleUIsService';
import { ruLocale } from '../lang/ruLocale';
import { RoleUI } from '../types/interfaces';
import { clearFilter } from '../utils';

export default function Roles() {
  const [role, setRole] = useState<RoleUI[]>([]);
  const [selectedRole, setSelectedRole] = useState<RoleUI>(RolesTemplate);
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    displayName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const [loading, setLoading] = useState<boolean>(true);
  const globalFilterRef = useRef<HTMLInputElement>(null);
  const nameFilterRef = useRef<HTMLInputElement>(null);
  const displayNameFilterRef = useRef<HTMLInputElement>(null);
  const descriptionFilterRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const [totalCount, setTotalCount] = useState(1);
  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);

  addLocale('ru', ruLocale.ru);
  locale('ru');

  const roleUIsService = useRoleUIsService();
  const getRoleUIsCallback = useCallback(() => {
    const sort = multiSortMeta?.find((x) => x !== undefined);
    const nameFilter = filters.name as DataTableFilterMetaData;
    const displayNameFilter = filters.displayName as DataTableFilterMetaData;
    const descriptionFilter = filters.description as DataTableFilterMetaData;
    const globalFilter = filters.global as DataTableFilterMetaData;

    const serviceResult = roleUIsService.getAll({
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify([
        {
          field: 'name',
          value: nameFilter.value,
          operator: nameFilter.matchMode
        },
        {
          field: 'displayName',
          value: displayNameFilter.value,
          operator: displayNameFilter.matchMode
        },
        {
          field: 'description',
          value: descriptionFilter.value,
          operator: descriptionFilter.matchMode
        },
        {
          field: 'global',
          value: globalFilter.value,
          operator: globalFilter.matchMode
        }
      ])
    });
    serviceResult.result.then((r) => {
      if (r) {
        setRole(r.itemsList);
        setTotalCount((r.total === 0 ? 1 : r.total) ?? 1);

      }
      setLoading(false);
    });
    return () => serviceResult.cancel();
  }, [filters, multiSortMeta, roleUIsService]);

  useEffect(() => {
    const cancel = getRoleUIsCallback();
    return () => cancel();
  }, [getRoleUIsCallback]);

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setFilters(_filters);
  };

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const header = (  
    <TablePageHeader
      tableName="roles"
      onGlobalSearchChange={value =>
        handleInputChange('global')({ target: { value } } as React.ChangeEvent<HTMLInputElement>)
      }
    />
  )

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  return (
    <div className="role-table wrapper wrapper--main">
      <Toast ref={toast} />

      <DataTable
        filters={filters}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedRole!}
        onSelectionChange={(e) => setSelectedRole(e.value)}
        onRowDoubleClick={(e) => {
          setSelectedRole(e.data as RoleUI);
        }}
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={role}
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        header={header}
        emptyMessage="Роли не найдены."
        onRowReorder={(e) => setRole(e.value)}
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
        footer={
          <div className="p-paginator p-component p-paginator-bottom justify-content-end" style={{
            height: 60
          }}>
            <span>{`Всего: ${role.length > 0 ? totalCount : 0}`}</span>
          </div>
        }
        pt={{
          footer: {
            style: {
              padding: 'unset',
              border: 'unset'
            }
          }
        }}
      >
        <Column
          columnKey="name"
          filter
          sortable
          field="name"
          header="Название"
          style={{ minWidth: '1rem', width: '35%' }}
          onFilterClear={() =>
            clearFilter('name', filters, setFilters, nameFilterRef)
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={nameFilterRef}
                onChange={handleInputChange('name')}
                placeholder="Поиск по названию роли"
              />
            </div>
          }
        />
        <Column
          columnKey="displayName"
          filter
          sortable
          field="displayName"
          header="Полное название"
          style={{ minWidth: '1rem', width: '35%' }}
          onFilterClear={() =>
            clearFilter(
              'displayName',
              filters,
              setFilters,
              displayNameFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={displayNameFilterRef}
                onChange={handleInputChange('displayName')}
                placeholder="Поиск по отображаемому названию роли"
              />
            </div>
          }
        />
        <Column
          columnKey="description"
          filter
          sortable
          field="description"
          header="Описание"
          style={{ minWidth: '1rem', width: '65%' }}
          onFilterClear={() =>
            clearFilter(
              'description',
              filters,
              setFilters,
              descriptionFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={descriptionFilterRef}
                onChange={handleInputChange('description')}
                placeholder="Поиск по описанию"
              />
            </div>
          }
        />
      </DataTable>
    </div>
  );
}
